// src/components/learning/LearningModule.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Home, Trophy, User, Plus, Lock, ChevronLeft, Search, CheckCircle, Book } from 'lucide-react';

// This component now uses your original logic and state management,
// mapped to the new UI template.
const LearningModule = ({ apiService }) => {
    const [module, setModule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);
    const [sectionStartTime, setSectionStartTime] = useState(null);
    const [sectionTimeSpent, setSectionTimeSpent] = useState({}); // Track time spent per section

    // --- All your original state and hooks are preserved ---
    const { learningData } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { moduleId } = useParams();
    const queryParams = new URLSearchParams(location.search);
    const topicParam = queryParams.get('topic');

    // --- YOUR ORIGINAL DATA FETCHING & ROUTING LOGIC ---
    // This entire block of code is taken directly from the file you provided
    // to ensure routing and data handling work exactly as you designed.
    const getTopicFromModuleId = (moduleId) => {
        if (topicParam) return topicParam;
        const mapping = {
            'phishing-detection': 'phishing_social_engineering', 'phishing_social_engineering': 'phishing_social_engineering',
            'password-security': 'password_security', 'password_security': 'password_security',
            'malware-protection': 'malware_ransomware', 'malware-identification': 'malware_ransomware', 'malware_ransomware': 'malware_ransomware',
            'network-security': 'network_security', 'network_security': 'network_security',
            'data-protection': 'data_protection', 'data_protection': 'data_protection',
            'mobile-security': 'mobile_security', 'mobile_security': 'mobile_security',
            'incident-response': 'incident_response', 'incident_response': 'incident_response'
        };
        if (mapping[moduleId]) return mapping[moduleId];
        if (moduleId && moduleId.includes('-') && moduleId.length > 20) return 'phishing_social_engineering';
        return moduleId || 'phishing_social_engineering';
    };

    const createModuleFromDBContent = (topic, dbData) => {
        console.log('LEARNING DEBUG] createModuleFromDBContent called with topic:', topic);
        console.log('LEARNING DEBUG] dbData structure:', dbData);

        if (dbData.resources && typeof dbData.resources === 'object') {
            const sections = [];

            Object.entries(dbData.resources).forEach(([resourceType, resources]) => {
                if (!Array.isArray(resources)) return;

                resources.forEach(resource => {
                    const rawContent = resource.content || resource.content_text || '';
                    const contentUrl = resource.content_url || resource.url || null;

                    let displayContent = '';

                    // ────── YOUTUBE VIDEO EMBED HANDLING ──────
                    if (contentUrl && (contentUrl.includes('youtube.com') || contentUrl.includes('youtu.be'))) {
                        let videoId = '';
                        if (contentUrl.includes('youtube.com/watch?v=')) {
                            videoId = new URL(contentUrl).searchParams.get('v');
                        } else if (contentUrl.includes('youtu.be/')) {
                            videoId = contentUrl.split('youtu.be/')[1].split('?')[0];
                        }

                        if (videoId) {
                            displayContent = `
                                <div class="text-center p-10 bg-gradient-to-b from-[#0d2a50] to-[#05091a] rounded-xl border border-[#00aaff]/30 shadow-lg">
                                    <h3 class="text-2xl font-bold text-[#00aaff] mb-6">${resource.title}</h3>
                                    <p class="text-gray-300 mb-8 max-w-2xl mx-auto">${resource.description || 'Watch this expert video to deepen your understanding.'}</p>
                                    <div class="relative w-full" style="padding-bottom: 56.25%;">
                                        <iframe 
                                            class="absolute top-0 left-0 w-full h-full rounded-lg"
                                            src="https://www.youtube-nocookie.com/embed/${videoId}?rel=0"
                                            title="${resource.title}"
                                            frameborder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowfullscreen>
                                        </iframe>
                                    </div>
                                </div>
                            `;
                        }
                    }
                    // ────── FALLBACK TO TEXT OR NON-YOUTUBE LINK ──────
                    else if (rawContent) {
                        displayContent = rawContent;
                    } else if (contentUrl) {
                        displayContent = `
                            <div class="text-center p-10 bg-gradient-to-b from-[#0d2a50] to-[#05091a] rounded-xl border border-[#00aaff]/30 shadow-lg">
                                <h3 class="text-2xl font-bold text-[#00aaff] mb-4">${resource.title}</h3>
                                <p class="text-gray-300 mb-8 max-w-2xl mx-auto">${resource.description || 'Watch this expert video to deepen your understanding.'}</p>
                                <a href="${contentUrl}" target="_blank" rel="noopener noreferrer"
                                   class="inline-flex items-center gap-3 px-8 py-4 bg-[#00aaff] text-black font-bold text-lg rounded-lg hover:bg-[#00ddff] transition-all transform hover:scale-105 shadow-xl">
                                   <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                     <path d="M8 5v14l11-7L8 5z"/>
                                   </svg>
                                   Watch ${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}
                                </a>
                                ${resource.author ? `<p class="mt-6 text-gray-400">By <span class="text-[#00aaff]">${resource.author}</span></p>` : ''}
                                ${resource.source ? `<p class="text-sm text-gray-500">Source: ${resource.source}</p>` : ''}
                            </div>
                        `;
                    } else {
                        displayContent = `
                            <div class="text-center py-16">
                                <h3 class="text-2xl font-bold text-white mb-4">${resource.title}</h3>
                                <p class="text-gray-400">${resource.description || 'No content available for this resource.'}</p>
                            </div>
                        `;
                    }

                    const metadata = `
                        <div class="mb-8 p-5 bg-[#0d2a50]/60 rounded-xl border border-[#00aaff]/30 backdrop-blur-sm">
                            <div class="flex flex-wrap items-center gap-4 text-sm">
                                ${resource.is_featured ? '<span class="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full font-semibold text-xs">Featured</span>' : ''}
                                <span class="px-3 py-1 bg-[#00aaff]/20 text-[#00aaff] rounded-full font-medium">${resourceType}</span>
                                <span class="text-gray-300">Difficulty: <strong class="text-[#00aaff]">${(resource.difficulty_level || resource.difficulty || 'Beginner').charAt(0).toUpperCase() + (resource.difficulty_level || resource.difficulty || 'beginner').slice(1)}</strong></span>
                                <span class="text-gray-300">Time: <strong class="text-[#00aaff]">${resource.estimated_read_time || resource.estimatedReadTime || 10} min</strong></span>
                                ${resource.tags && resource.tags.length > 0
                            ? `<span class="text-gray-400">Tags: ${resource.tags.map(t => `<span class="text-[#00aaff]">#${t}</span>`).join(' ')}</span>`
                            : ''
                        }
                            </div>
                            ${resource.publicationDate ? `<p class="text-xs text-gray-500 mt-3">Published: ${new Date(resource.publicationDate).toLocaleDateString()}</p>` : ''}
                        </div>
                    `;

                    sections.push({
                        id: resource.id,
                        title: resource.title,
                        type: resourceType,
                        content: metadata + displayContent,
                        completed: !!resource.completed,
                    });
                });
            });

            return {
                id: topic,
                title: dbData.topicName || topic.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                description: dbData.topicName
                    ? `Master ${dbData.topicName.toLowerCase()} with expert-curated content`
                    : 'Advanced cybersecurity training module',
                sections: sections
            };
        }

        // CASE 2: Legacy module from /learning/modules/:id (content_modules + content_sections)
        if (dbData.sections && Array.isArray(dbData.sections)) {
            const sections = dbData.sections
                .filter(section => section.type !== 'quiz')
                .map(section => ({
                    id: section.id || `legacy-${Math.random().toString(36).substr(2, 9)}`,
                    title: section.title,
                    type: section.type || 'article',
                    content: typeof section.content === 'string'
                        ? section.content
                        : section.content?.text || section.content || '<p>No content available.</p>',
                    completed: section.completed || false,
                }));

            return {
                id: dbData.id || topic,
                title: dbData.title || 'Learning Module',
                description: dbData.description || 'Training content',
                sections
            };
        }

        // Final fallback
        console.warn('LEARNING DEBUG] No valid content structure found in dbData');
        return null;
    };

    function isValidUUID(uuid) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    }

    useEffect(() => {
        const fetchModule = async () => {
            setLoading(true);
            console.log('LEARNING DEBUG] Starting fetch...');
            console.log('LEARNING DEBUG] moduleId:', moduleId);
            console.log('LEARNING DEBUG] topicParam:', topicParam);

            try {
                let moduleData = null;

                const getDBContentByModuleId = async (id) => {
                    if (!id) return null;
                    console.log(`LEARNING DEBUG] Trying /learning/modules/${id}`);
                    try {
                        const response = await apiService.get(`/learning/modules/${id}`);
                        console.log('LEARNING DEBUG] /modules response:', response);
                        if (response.success && response.data) {
                            const topic = getTopicFromModuleId(id);
                            return createModuleFromDBContent(topic, response.data);
                        }
                        return null;
                    } catch (e) {
                        console.warn('LEARNING DEBUG] /modules failed, trying topic route...', e);
                        const mappedTopic = getTopicFromModuleId(id);
                        return await getDBContentForTopic(mappedTopic);
                    }
                };

                const getDBContentForTopic = async (topic) => {
                    if (!topic) return null;
                    console.log(`LEARNING DEBUG] Trying /learning/topic/${topic}`);
                    try {
                        const response = await apiService.get(`/learning/topic/${topic}`);
                        console.log('LEARNING DEBUG] /topic response:', response);
                        console.log('LEARNING DEBUG] Raw response.data.resources:', response.data?.resources);
                        console.log('LEARNING DEBUG] Resource types found:', response.data?.resourceTypes);
                        if (response.success && response.data) {
                            return createModuleFromDBContent(topic, response.data);
                        }
                        return null;
                    } catch (e) {
                        console.error('LEARNING DEBUG] /topic failed:', e);
                        return null;
                    }
                };

                // FORCE USE OF THE NEW /topic/ ROUTE — THIS IS WHERE YOUR REAL DATA LIVES
                const effectiveTopic = topicParam || getTopicFromModuleId(moduleId);

                if (effectiveTopic) {
                    console.log('LEARNING DEBUG] Using NEW topic route for:', effectiveTopic);
                    moduleData = await getDBContentForTopic(effectiveTopic);
                } else if (moduleId && isValidUUID(moduleId)) {
                    // Only try old /modules/ route if it's a real UUID (not a topic slug)
                    console.log('LEARNING DEBUG] Using legacy /modules/ route for UUID:', moduleId);
                    moduleData = await getDBContentByModuleId(moduleId);
                }

                console.log('LEARNING DEBUG] Final moduleData:', moduleData);

                if (moduleData && moduleData.sections && moduleData.sections.length > 0) {
                    console.log('LEARNING DEBUG] Success! Loaded', moduleData.sections.length, 'sections');
                    setModule(moduleData);
                    const completedSections = moduleData.sections.filter(s => s.completed).length;
                    const totalSections = moduleData.sections.length;
                    const progressPercentage = totalSections > 0 ? (completedSections / totalSections) * 100 : 0;
                    setProgress(progressPercentage);
                    setIsCompleted(progressPercentage === 100);
                    setActiveSection(0);
                    // Initialize section start time
                    setSectionStartTime(Date.now());
                } else {
                    console.log('LEARNING DEBUG] No valid sections found → showing fallback or not found');
                    setModule(null);
                }
            } catch (error) {
                console.error('LEARNING DEBUG] Fatal error in fetchModule:', error);
                setModule(null);
            }
            setLoading(false);
        };
        fetchModule();
    }, [moduleId, topicParam, apiService]);

    // Track time when section changes
    useEffect(() => {
        if (module && module.sections && module.sections.length > 0) {
            // Record time spent on previous section
            if (sectionStartTime !== null) {
                const timeSpent = Math.floor((Date.now() - sectionStartTime) / 1000); // in seconds
                const prevSectionId = module.sections[activeSection - 1]?.id;
                if (prevSectionId && timeSpent > 0) {
                    setSectionTimeSpent(prev => ({
                        ...prev,
                        [prevSectionId]: (prev[prevSectionId] || 0) + timeSpent
                    }));
                }
            }
            // Start timer for current section
            setSectionStartTime(Date.now());
        }
    }, [activeSection, module]);

    const handleSectionCompletion = async () => {
        const currentSection = module.sections[activeSection];
        if (currentSection.completed) return; // Don't re-complete

        // Calculate time spent on current section
        const timeSpentOnSection = sectionStartTime
            ? Math.floor((Date.now() - sectionStartTime) / 1000) // in seconds
            : 0;

        // Update local time tracking
        const updatedTimeSpent = {
            ...sectionTimeSpent,
            [currentSection.id]: (sectionTimeSpent[currentSection.id] || 0) + timeSpentOnSection
        };
        setSectionTimeSpent(updatedTimeSpent);

        const updatedSections = module.sections.map((sec, index) =>
            index === activeSection ? { ...sec, completed: true } : sec
        );
        const updatedModule = { ...module, sections: updatedSections };
        setModule(updatedModule);

        const completedCount = updatedSections.filter(s => s.completed).length;
        const totalSections = updatedSections.length;
        const newProgress = totalSections > 0 ? Math.round((completedCount / totalSections) * 100) : 0;
        setProgress(newProgress);
        if (newProgress === 100) setIsCompleted(true);

        try {
            const topicForProgress = getTopicFromModuleId(moduleId) || topicParam;
            if (apiService) {
                // Send progress update with time spent
                await apiService.post('/learning/progress', {
                    resourceId: currentSection.id,
                    moduleId: moduleId,
                    topic: topicForProgress,
                    completed: true,
                    timeSpent: timeSpentOnSection,
                    completionPercentage: newProgress
                });
            }
        } catch (error) {
            console.error('Failed to update progress on backend:', error);
            // Optionally revert state on failure
        }

        // Reset timer for next section
        setSectionStartTime(Date.now());
    };

    const handleMainAction = () => {
        // This function combines completion and navigation
        handleSectionCompletion(); // Mark current as complete

        const isLastSection = activeSection === module.sections.length - 1;
        if (isLastSection) {
            // Your original navigation logic for when the module is finished
            const topicId = module.id || getTopicFromModuleId(moduleId);
            navigate(`/challenges?arena=${topicId}`);
        } else {
            // Move to the next section
            setActiveSection(activeSection + 1);
        }
    };

    const handlePreviousSection = () => {
        if (activeSection > 0) {
            setActiveSection(activeSection - 1);
        }
    };

    // --- UI Rendering ---

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-[#05091a] text-white">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#00aaff]"></div>
            </div>
        );
    }

    if (!module) {
        return (
            <div className="flex flex-col justify-center items-center h-screen bg-[#05091a] text-white p-8">
                <h1 className="text-3xl font-bold mb-4 text-[#00aaff]">Module Not Found</h1>
                <p className="text-lg text-gray-400 mb-8">The requested learning content could not be loaded. It may not exist or there was an issue fetching it.</p>
                <button
                    onClick={() => navigate('/dashboard')}
                    className="bg-[#0d2a50] text-white font-semibold px-6 py-3 rounded-md border border-[#00aaff] hover:bg-[#00aaff] hover:text-black transition-all"
                >
                    Return to Dashboard
                </button>
            </div>
        );
    }

    const currentSection = module.sections[activeSection];

    // Map module IDs to specific images
    const topicImageMapping = {
        'phishing_social_engineering': '/assets/images/characters/Shanaya2.png',
        'password_security': '/assets/images/characters/Shanaya2.png',
        'malware_ransomware': '/assets/images/characters/Shanaya2.png',
        // Fallback image for other topics
        'default': '/assets/images/characters/Shanaya2.png'
    };

    // Get the appropriate image URL for the current module
    const imageUrl = topicImageMapping[module.id] || topicImageMapping.default;

    // --- NEW JSX STRUCTURE WITH YOUR LOGIC APPLIED ---
    return (
        <>
            <style>{`
            .prose-content p {
                margin-bottom: 1rem;
            }
            .prose-content h1, .prose-content h2, .prose-content h3, .prose-content h4 {
                font-weight: 600;
                margin-top: 1.5em;
                margin-bottom: 1em;
                color: #ffffff;
            }
            .prose-content ul, .prose-content ol {
                margin-left: 1.5rem;
                margin-bottom: 1rem;
                list-style-position: inside;
            }
            .prose-content li {
                margin-bottom: 0.5rem;
            }
            .prose-content a {
                color: #00aaff;
                text-decoration: underline;
            }
        `}</style>
            <div className="min-h-screen bg-[#05091a] text-gray-300 font-sans flex flex-col lg:flex-row p-3 sm:p-4"
                style={{
                    backgroundImage: `radial-gradient(circle at top left, rgba(0, 170, 255, 0.1), transparent 30%),
                                 radial-gradient(circle at bottom right, rgba(0, 170, 255, 0.1), transparent 30%)`
                }}>


                <nav className="flex flex-row lg:flex-col items-center justify-between py-3 lg:py-6 px-3 bg-black bg-opacity-20 rounded-lg">
                    <div className="flex flex-row lg:flex-col items-center gap-4 lg:gap-8">
                        <div className="mb-0 lg:mb-12 cursor-pointer" onClick={() => navigate('/dashboard')}>
                            <img
                                src={'/assets/images/logo.png'}
                                alt="User Avatar"
                                className="w-10 h-10 lg:w-16 lg:h-16 cursor-pointer  hover:border-blue-500 transition-all"
                            />
                        </div>
                        <button onClick={() => navigate('/dashboard')} className="p-2 text-gray-500 hover:text-[#00aaff] transition-colors"><Home size={24} /></button>
                        <button onClick={() => navigate('/learning')} className="p-2 text-[#00aaff] rounded-md bg-[#00aaff] bg-opacity-20"><Book size={24} /></button>
                        {/* <button onClick={() => navigate('/challenges')} className="p-2 text-gray-500 hover:text-[#00aaff] transition-colors"><Trophy size={24} /></button> */}
                        <button onClick={() => navigate('/profile')} className="p-2 text-gray-500 hover:text-[#00aaff] transition-colors"><User size={24} /></button>
                    </div>
                    <button className="p-2 text-gray-500 hover:text-[#00aaff] transition-colors"><Plus size={24} /></button>
                </nav>

                <main className="flex-1 grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6 mt-4 lg:mt-0 lg:ml-4">
                    <div className="xl:col-span-2 flex flex-col p-3 sm:p-6">
                        <div className="flex-shrink-0 mb-6 text-center">
                            <h2 className="text-lg font-semibold text-gray-500 uppercase tracking-wider">Learning Hub</h2>
                            <h1 className="text-2xl sm:text-4xl font-bold text-white mt-2">{module.title}</h1>
                            <p className="text-gray-400 mt-2 max-w-3xl mx-auto">{module.description}</p>
                        </div>

                        <div className="flex-grow bg-black/20 border border-white/10 rounded-lg p-6 overflow-y-auto min-h-0">
                            <div
                                className="prose-content"
                                dangerouslySetInnerHTML={{ __html: currentSection.content }}
                            />
                        </div>

                        <div className="flex items-center justify-center gap-4 flex-shrink-0 mt-6">
                            <button
                                onClick={handlePreviousSection}
                                disabled={activeSection === 0}
                                className="p-3 bg-[#0d2a50] rounded-md border border-[#00aaff]/50 text-[#00aaff] hover:bg-[#00aaff]/20 disabled:opacity-50 transition-all"
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                onClick={handleMainAction}
                                className="px-8 py-3 bg-[#0d2a50] rounded-md border border-[#00aaff] text-white font-semibold hover:bg-[#00aaff] hover:text-black transition-all"
                            >
                                {activeSection === module.sections.length - 1 ? 'RETURN TO ARENAS' : 'COMPLETE & CONTINUE'}
                            </button>
                        </div>
                    </div>

                    <aside className="xl:col-span-1 bg-black bg-opacity-20 rounded-lg p-4 sm:p-6 flex flex-col">
                        <div className="relative mb-8">
                            <input type="text" placeholder="Search modules..." className="w-full bg-[#0d2a50] border border-[#00aaff]/30 rounded-md py-2 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00aaff]" />
                            <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        </div>

                        <div className="w-full h-40 rounded-lg overflow-hidden mb-8 border border-[#00aaff]/30">
                            <img
                                src={imageUrl}
                                alt={module.title}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        <div className="mb-8">
                            <h3 className="font-semibold text-gray-400 mb-4 text-center">PROGRESS</h3>
                            <div className="flex items-center justify-center">
                                <div className="relative w-40 h-40">
                                    <svg className="w-full h-full" viewBox="0 0 100 100">
                                        <circle className="text-[#0d2a50]" strokeWidth="10" stroke="currentColor" fill="transparent" r="45" cx="50" cy="50" />
                                        <circle
                                            className="text-orange-400" strokeWidth="10"
                                            strokeDasharray={2 * Math.PI * 45}
                                            strokeDashoffset={(2 * Math.PI * 45) - (progress / 100) * (2 * Math.PI * 45)}
                                            strokeLinecap="round" stroke="currentColor" fill="transparent"
                                            r="45" cx="50" cy="50"
                                            style={{ transition: 'stroke-dashoffset 0.5s, color 0.5s', transform: 'rotate(-90deg)', transformOrigin: 'center', filter: 'drop-shadow(0 0 4px currentColor)' }}
                                        />
                                    </svg>
                                    <span className="absolute inset-0 flex items-center justify-center text-3xl font-bold text-white">{`${Math.round(progress)}%`}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex-grow flex flex-col min-h-0">
                            <h3 className="font-semibold text-gray-400 mb-4 text-center">MODULE CONTENT</h3>
                            <div className="space-y-2 overflow-y-auto">
                                {module.sections.map((section, index) => (
                                    <button
                                        key={section.id}
                                        onClick={() => setActiveSection(index)}
                                        className={`w-full text-left p-3 rounded-md flex items-center gap-3 transition-all ${activeSection === index ? 'bg-[#00aaff]/20' : 'bg-[#0d2a50] hover:bg-[#0d2a50]/60'}`}
                                    >
                                        {section.completed ? <CheckCircle size={18} className="text-green-400" /> : <div className="w-4 h-4 border-2 border-gray-500 rounded-full" />}
                                        <span>{section.title}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={() => navigate('/dashboard')}
                            className="w-full mt-6 py-3 bg-[#0d2a50] rounded-md border border-[#00aaff]/50 text-white font-semibold hover:bg-[#00aaff]/20 transition-all flex-shrink-0"
                        >
                            RETURN TO ARENA
                        </button>
                    </aside>
                </main>
            </div>
        </>
    );
};

export default LearningModule;